<template>
  <v-layout
    column
    class="nx-searcher-list"
  >
    <nx-search-results-toolbar
      :actions="availableActions"
      :bulk-selection="bulkSelection && !selectable"
      :check-full-search="checkFullSearch"
      :global-actions="globalActions"
      :loading="loading && items.length === 0"
      :num-results="items.length"
      :num-selected="selectedItems.length"
      :search-limit-options="searchLimitOptions"
      :search-limit="searchLimit"
      :sort-filters="sortFilters"
      :sort="sort"
      :total-items="totalItems"
      :view="view"
      :views="views"
      @click:action="showConfirmSelectionModal"
      @click:refresh="$emit('click:refresh')"
      @input:cancel-full-search="uncheckAll"
      @input:check-all="selectedItems = items"
      @input:check-full-search="checkFullSearch = true"
      @input:search-limit="$emit('input:search-limit', $event)"
      @input:sort-filters="$emit('input:sort-filters', $event)"
      @input:uncheck-all="uncheckAll"
      @input:view="$emit('input:view', $event)"
    />

    <v-expand-transition origin="center top">
      <ds-alert
        v-show="searchResultsHaveChanged"
        tag="a"
        @click="$emit('click:refresh')"
      >
        <ds-text
          size="300"
          class="result-changed-alert"
        >
          <span
            v-if="searchResultsDiff > 0"
            v-html="searchResultsChangedText"
          />
          <span v-else>{{ searchResultsChangedText }}</span>
        </ds-text>
      </ds-alert>
    </v-expand-transition>

    <nx-list
      :loading="loading && items.length === 0"
      :bulk-selection="bulkSelection || multipleSelection"
      :selected-items-ids="selectedItemsIds"
      :checked="checkFullSearch"
      :items="items"
      :mosaic="mosaic"
      :normal="normal"
      :condensed="condensed"
      :selectable="selectable"
      @click:item="onClickItem"
      @input:check="onCheckItem"
      @input:uncheck="onUncheckItem"
      @input:add-filter="$emit('input:add-filter', $event)"
      @input:set-filter="$emit('input:set-filter', $event)"
    >
      <template #item-actions="props">
        <v-layout
          fill-height
          justify-end
        >
          <v-flex
            class="fill-height justify-end"
            :align-self-end="mosaic"
            :align-self-start="normal"
            :align-self-center="condensed"
          >
            <nx-searcher-list-actions
              v-bind="props"
              :mosaic="mosaic"
              v-on="$listeners"
            />
          </v-flex>
        </v-layout>
      </template>
    </nx-list>

    <nx-searcher-selection-confirm-dialog
      ref="confirmDialog"
      :selected-items="selectedItems"
      :total-items="checkFullSearch ? totalItems : selectedItems.length"
      :use-search-config="checkFullSearch"
      @continue="showActionModal"
    />

    <nx-searcher-action-dialog
      ref="actionDialog"
      :entity="entity"
      :entity-ids="selectedItemsIds"
      :filters="filters"
      :sort="sort"
      :use-search-config="checkFullSearch"
      @success="handleActionSuccess"
    />
  </v-layout>
</template>

<script>
import NxSearchResultsToolbar from '@/components/searcher/SearchResultsToolbar';
import NxList from '@/components/list/List';
import NxSearcherListActions from '@/components/list/components/ListItemActions';
import NxSearcherActionDialog from '@/modules/searcher/actions/SearcherActionDialog';
import { LIST_VIEW_CONDENSED, LIST_VIEW_MOSAIC, LIST_VIEW_NORMAL } from '@/modules/searcher/config';
import NxSearcherSelectionConfirmDialog from '@/modules/searcher/selection/SearcherSelectionConfirmDialog.vue';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import { toArray } from '@/utils';

export default {
  components: {
    NxSearcherSelectionConfirmDialog,
    NxSearcherActionDialog,
    NxSearchResultsToolbar,
    NxList,
    NxSearcherListActions,
  },
  props: {
    actions: {
      type: Array,
      default: () => ([]),
    },
    bulkSelection: Boolean,
    entity: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    globalActions: {
      type: Array,
      default: () => ([]),
    },
    items: {
      type: Array,
      default: () => ([]),
    },
    loading: Boolean,
    multipleSelection: Boolean,
    selectable: Boolean,
    selection: {
      type: Array,
      default: () => [],
    },
    searchResultsHaveChanged: Boolean,
    searchResultsDiff: {
      type: Number,
      default: 0,
    },
    searchLimitOptions: {
      type: Array,
      default: () => ([]),
    },
    searchLimit: {
      type: Number,
      required: true,
    },
    sort: {
      type: Object,
      required: true,
    },
    sortFilters: {
      type: Array,
      required: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    view: {
      type: Object,
      default: () => ([]),
    },
  },
  data() {
    return {
      checkFullSearch: false,
      selectedItems: [],
      views: [
        LIST_VIEW_NORMAL,
        LIST_VIEW_CONDENSED,
        LIST_VIEW_MOSAIC,
      ],
    };
  },
  computed: {
    availableActions() {
      return this.actions.filter((action) => {
        if (isEmpty(action.filterEntities)) {
          return true;
        }
        // Filter the available actions by filterEntities configuration
        const entries = Object.entries(action.filterEntities);
        if (this.checkFullSearch) {
          // All current filters should be covered by action filterEntities
          return entries.every(([filter, filterValue]) => {
            const currentValue = this.filters[filter];
            if (isArray(currentValue)) {
              return currentValue.every(currentValueItem => toArray(filterValue).includes(currentValueItem));
            }
            return toArray(filterValue).includes(currentValue);
          });
        }

        // All selected entities should be covered by action filterEntities
        return this.selectedItems.every(item => entries.every(([filter, filterValue]) => {
          if (isArray(filterValue)) {
            return filterValue.includes(item[filter]);
          }

          if (isEqual(item[filter], filterValue)) {
            return true;
          }

          // Search in item params
          if (isObject(item.params)
            && isEqual(item.params[filter], filterValue)) {
            return true;
          }

          return false;
        }));
      });
    },
    normal() {
      return this.view.value === 'normal';
    },
    mosaic() {
      return this.view.value === 'mosaic';
    },
    condensed() {
      return this.view.value === 'condensed';
    },
    searchResultsChangedText() {
      if (this.searchResultsDiff > 0) {
        return this.$tc('message.searcher.searchHasNewResults', this.searchResultsDiff, { count: this.searchResultsDiff });
      }
      return this.$t('message.searcher.searchResultsHaveChanged');
    },
    selectedItemsIds() {
      return this.selectedItems.map(item => item.id);
    },
  },
  watch: {
    entity() {
      this.uncheckAll();
    },
    filters() {
      if (this.checkFullSearch) {
        this.uncheckAll();
      }
    },
    selection: {
      handler(val) {
        this.selectedItems = val;
      },
      immediate: true,
    },
  },
  mounted() {
    this.selectedItems = this.selection;
  },
  methods: {
    uncheckAll() {
      this.checkFullSearch = false;
      this.selectedItems = [];
    },
    onUncheckItem(item) {
      this.checkFullSearch = false;
      this.selectedItems = this.selectedItems.filter(i => i.id !== item.id);
      this.$emit('click:item', item);
    },
    onCheckItem(item) {
      this.selectedItems = [...this.selectedItems, item];
      this.$emit('click:item', item);
    },
    onClickItem(item) {
      if (this.selectable) {
        if (this.selectedItems.includes(item.id)) {
          this.selectedItems = this.selectedItems.filter(i => i.id !== item.id);
        } else {
          this.selectedItems = [...this.selectedItems, item];
        }
      }
      this.$emit('click:item', item);
    },
    showConfirmSelectionModal(action) {
      if (action.disableConfirmationStep === true) {
        this.showActionModal(action);
      } else {
        this.$refs.confirmDialog.open(action);
      }
    },
    showActionModal(action) {
      this.$refs.actionDialog.open(action);
    },
    handleActionSuccess() {
      this.toast.notify('message.list.actions.success');
      this.uncheckAll();
    },
  },
};
</script>

<style>
.nx-searcher-list {
  .ds-alert {
    margin-bottom: 4px
  }
}
</style>
